/**
 * config/personalDoc.js
 * Личный документ работника в Сейфе — конфигурация по стране организации.
 *
 * Зеркало backend/config/countries.js: там страна определяет промпт OCR,
 * здесь — форму, подписи и пропорции рамок. Оба списка должны меняться вместе.
 *
 * i18n: подписи берутся с префиксом (eid.* / passportRu.*). Ключи, которые для
 * страны не переопределены, падают на eid.* — там лежат страно-нейтральные
 * строки Сейфа (кнопки, ошибки, автоблокировка). Переопределять нужно только
 * то, где буквально написано «Emirates ID» или где отличается формат поля.
 */

// Пропорции рамки превью (ширина / высота):
//   Emirates ID — карточка ID-1, 85.6 × 54 мм;
//   паспорт РФ  — главный разворот (страницы 2–3), 176 × 125 мм.
const ASPECT_ID_CARD        = 85.6 / 54;    // ≈ 1.585
const ASPECT_PASSPORT_RU    = 176 / 125;    // ≈ 1.408

const DOC_TYPES = {
  emirates_id: {
    type: 'emirates_id',
    i18nPrefix: 'eid',
    aspect: ASPECT_ID_CARD,
    // Порядок полей формы = порядок в сетке 2×N
    fieldKeys: ['number', 'expiry', 'name_en', 'nationality'],
    // Плашка «истекает через N дней» имеет смысл только для документа со сроком
    tracksExpiry: true,
  },
  passport_ru: {
    type: 'passport_ru',
    i18nPrefix: 'passportRu',
    aspect: ASPECT_PASSPORT_RU,
    // Срок действия паспорта РФ на развороте не напечатан (действует до
    // достижения возраста), вместо него — дата выдачи и кем выдан.
    fieldKeys: ['number', 'issue_date', 'issued_by', 'name_en'],
    tracksExpiry: false,
  },
};

const COUNTRY_DOC = {
  AE: 'emirates_id',
  RU: 'passport_ru',
};

const DEFAULT_DOC = 'emirates_id';

/**
 * Страны в селекторе Настроек, в порядке показа.
 * Должен совпадать с COUNTRIES в backend/config/countries.js — иначе сервер
 * молча оставит прежнюю страну (валидация на бэке отбрасывает неизвестный код).
 */
export const COUNTRY_OPTIONS = ['AE', 'RU'];

/** Конфиг документа по коду страны организации (user.org_country). */
export function personalDocConfig(country) {
  const key = COUNTRY_DOC[String(country || '').toUpperCase()] || DEFAULT_DOC;
  return DOC_TYPES[key];
}

/**
 * Ключ поля → ключи i18n (подпись и плейсхолдер) БЕЗ префикса страны.
 * Полный ключ собирается как `${doc.i18nPrefix}.${label}` с падением на eid.*.
 */
export const DOC_FIELD_I18N = {
  number:      { label: 'fieldNumber',      ph: 'numberPlaceholder' },
  expiry:      { label: 'fieldExpiry',      ph: 'expiryPlaceholder' },
  name_en:     { label: 'fieldName',        ph: 'namePlaceholder' },
  nationality: { label: 'fieldNationality', ph: 'nationalityPlaceholder' },
  issue_date:  { label: 'fieldIssueDate',   ph: 'issueDatePlaceholder' },
  issued_by:   { label: 'fieldIssuedBy',    ph: 'issuedByPlaceholder' },
};

/** Все ключи полей, которые может хранить Сейф — для инициализации формы. */
export const ALL_DOC_FIELD_KEYS = ['number', 'expiry', 'name_en', 'nationality', 'issue_date', 'issued_by'];

/**
 * Куда центрировать карту, когда своих координат ещё нет: пустая карта Объектов,
 * пикер точки, отказ браузера в геолокации.
 *
 * Это такая же подсказка, как плейсхолдер адреса, только картой: РФ-тенант,
 * открывший пикер, не должен приземляться в Персидский залив. Дефолт — Дубай,
 * страна по умолчанию AE (backend/config/countries.js).
 */
const COUNTRY_CENTER = Object.freeze({
  AE: Object.freeze({ lat: 25.2048, lng: 55.2708 }),   // Дубай
  RU: Object.freeze({ lat: 55.7558, lng: 37.6173 }),   // Москва, Красная площадь
});

/**
 * ⚠️ Возвращает ссылку на константу, а НЕ новый литерал. Это намеренно:
 * результат кладут в зависимости useEffect инициализации карты, и свежий объект
 * на каждый рендер пересоздавал бы карту в цикле. Не переписывать на `{ ...x }`.
 */
export function countryCenter(country) {
  return COUNTRY_CENTER[String(country || '').toUpperCase()] || COUNTRY_CENTER.AE;
}

export { DOC_TYPES, COUNTRY_DOC };
